// utils/setupValidators.js

/* ---------------- Profile validators ---------------- */
export const PROFILE_REQUIRED = [
  "email",
  "restaurant_name",
  "first_name",
  "last_name",
  "phone_number",
  "vat_number",
  "street",
  "house_number",
  "postal_code",
  "city",
  "country",
];

export const PROFILE_VALIDATORS = {
  email: (a) => typeof a?.email === "string" && /\S+@\S+\.\S+/.test(a.email),
  restaurant_name: (a) => {
    const v = a?.restaurant_name || a?.restaurantName;
    return typeof v === "string" && v.trim().length > 1;
  },
  first_name: (a) =>
    typeof a?.first_name === "string" && a.first_name.trim().length > 0,
  last_name: (a) =>
    typeof a?.last_name === "string" && a.last_name.trim().length > 0,
  phone_number: (a) =>
    typeof a?.phone_number === "string" && a.phone_number.trim().length > 0,
  vat_number: (a) =>
    typeof a?.vat_number === "string" && a.vat_number.trim().length > 0,
  street: (a) => typeof a?.street === "string" && a.street.trim().length > 0,
  house_number: (a) =>
    typeof a?.house_number === "string" && a.house_number.trim().length > 0,
  postal_code: (a) =>
    typeof a?.postal_code === "string" && a.postal_code.trim().length > 0,
  city: (a) => typeof a?.city === "string" && a.city.trim().length > 0,
  country: (a) => typeof a?.country === "string" && a.country.trim().length > 0,
};

/* ---------------- Small time helpers (shared) ---------------- */
const isHHmm = (t) => typeof t === "string" && /^\d{2}:\d{2}$/.test(t);
const toMin = (t) => {
  const [h, m] = String(t || "0:0")
    .split(":")
    .map(Number);
  return h * 60 + m;
};

// More tolerant parser for the “any shift” validator
const parseTimeFlexible = (t) => {
  if (t == null) return null;
  if (typeof t === "number") {
    const hh = Math.floor(t / 100);
    const mm = t % 100;
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
    return hh * 60 + mm;
  }
  const s = String(t).trim();
  let m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) {
    const hh = +m[1],
      mm = +m[2];
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
    return hh * 60 + mm;
  }
  m = s.match(/^(\d{3,4})$/); // 800, 0815, 930
  if (m) {
    const num = +m[1],
      hh = Math.floor(num / 100),
      mm = num % 100;
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
    return hh * 60 + mm;
  }
  return null;
};

const pickStart = (s) =>
  s?.startTime ??
  s?.startHour ??
  s?.start ??
  s?.from ??
  s?.open ??
  s?.timeStart ??
  s?.start_time ??
  null;
const pickEnd = (s) =>
  s?.endTime ??
  s?.endHour ??
  s?.end ??
  s?.to ??
  s?.close ??
  s?.timeEnd ??
  s?.end_time ??
  null;
const pickOn = (s) => (s?.enabled ?? s?.on ?? s?.active ?? true) !== false;

/* ---------------- Opening hours validator (per meal, as you had) ---------------- */
// NOTE: kept for compatibility where you validate a single meal’s “days” object.
// Slightly more tolerant (supports startHour/endHour).
const MIN_ENABLED_DAYS = 1;

export function validateOpeningHours(raw) {
  if (!raw || typeof raw !== "object") return { done: false, hint: "not set" };
  const ss = raw.schemeSettings || raw.scheme_settings || raw.days || raw;
  if (!ss || typeof ss !== "object") return { done: false, hint: "not set" };

  let enabledCount = 0;
  const issues = [];

  for (const [day, cfg] of Object.entries(ss)) {
    if (!cfg || typeof cfg !== "object") continue;
    if (cfg.enabled) {
      enabledCount++;
      const s = String(pickStart(cfg) || "").trim();
      const e = String(pickEnd(cfg) || "").trim();

      if (!isHHmm(s) || !isHHmm(e)) issues.push(`${day}: invalid time`);
      else if (toMin(s) >= toMin(e)) issues.push(`${day}: start ≥ end`);

      if (cfg.maxCapacityEnabled) {
        const n = Number(cfg.maxCapacity);
        if (!Number.isFinite(n) || n <= 0)
          issues.push(`${day}: capacity invalid`);
      }
    }
  }

  if (enabledCount < MIN_ENABLED_DAYS) {
    issues.unshift(
      `enable at least ${MIN_ENABLED_DAYS} day${
        MIN_ENABLED_DAYS > 1 ? "s" : ""
      }`
    );
  }

  const done = issues.length === 0;
  const hint = done
    ? "done"
    : `issues: ${issues.slice(0, 3).join(", ")}${issues.length > 3 ? "…" : ""}`;
  return { done, hint };
}

/* ---------------- Any-shift-in-the-week validator (NEW) ----------------
   Passes if there exists at least one valid shift (start < end) anywhere
   in breakfast OR lunch OR dinner. Ignores what day it is.
----------------------------------------------------------------------- */
function scanMealForAnyValidShift(meal) {
  let sawInvalid = false;
  let sawStartGte = false;

  const visit = (node) => {
    if (!node) return false;

    if (Array.isArray(node)) {
      for (const x of node) if (visit(x)) return true;
      return false;
    }

    if (typeof node === "object") {
      // If this object looks like a “slot”, try to validate it
      const stRaw = pickStart(node);
      const enRaw = pickEnd(node);
      const enabled = pickOn(node);

      if (stRaw != null || enRaw != null) {
        const st = parseTimeFlexible(stRaw);
        const en = parseTimeFlexible(enRaw);
        if (!enabled) {
          // ignore disabled
        } else if (st == null || en == null) {
          sawInvalid = true;
        } else if (st >= en) {
          sawStartGte = true;
        } else {
          return true; // found one valid shift
        }
      }

      // Try common containers: schemeSettings/days/week/slots or weekday keys
      const containers = [
        node.schemeSettings,
        node.scheme_settings,
        node.days,
        node.week,
        node.slots,
        node, // also walk everything to catch weekday keys (mon/tue/...)
      ];

      for (const c of containers) {
        if (!c) continue;
        if (Array.isArray(c)) {
          for (const x of c) if (visit(x)) return true;
        } else if (typeof c === "object") {
          for (const k of Object.keys(c)) {
            if (visit(c[k])) return true;
          }
        }
      }
    }

    return false;
  };

  const found = visit(meal);
  return { found, sawInvalid, sawStartGte };
}

/** NEW export: pass if there's at least ONE valid shift anywhere in the week */
export function validateOpeningHoursAnyShiftWeek(openingHoursAll) {
  const breakfast = openingHoursAll?.breakfast ?? null;
  const lunch = openingHoursAll?.lunch ?? null;
  const dinner = openingHoursAll?.dinner ?? null;

  // Scan meals in order: breakfast, lunch, dinner
  const results = [breakfast, lunch, dinner].map(scanMealForAnyValidShift);
  const found = results.some((r) => r.found);
  const sawStartGte = results.some((r) => r.sawStartGte);
  const sawInvalid = results.some((r) => r.sawInvalid);

  if (found) return { done: true, hint: "done" };
  if (sawStartGte) return { done: false, hint: "issues: start ≥ end" };
  if (sawInvalid) return { done: false, hint: "issues: invalid time" };

  // Nothing time-like found at all
  return { done: false, hint: "issues: add at least one shift" };
}

/* ---------------- Settings validator ---------------- */
const isNonNegInt = (n) => Number.isInteger(Number(n)) && Number(n) >= 0;
const isPosInt = (n) => Number.isInteger(Number(n)) && Number(n) > 0;
const isYesNo = (s) => s === "Ja" || s === "Nee";
const isPhoneish = (s) =>
  typeof s === "string" && /^[+0-9][0-9\s\-().]{6,}$/.test(s || "");
const isHHmmOrEmpty = (t) =>
  t === "" || (typeof t === "string" && /^\d{2}:\d{2}$/.test(t));
const GUEST_MOD_ALLOWED = new Set(["never", "always", "until12h"]);

export function validateSettings(raw) {
  if (!raw || typeof raw !== "object") return { done: false, hint: "not set" };

  const issues = [];

  if (!isPosInt(raw.zitplaatsen)) issues.push("zitplaatsen");
  if (!isNonNegInt(raw.uurOpVoorhand)) issues.push("uurOpVoorhand");
  if (!isNonNegInt(raw.dagenInToekomst)) issues.push("dagenInToekomst");
  if (!isPosInt(raw.maxGasten)) issues.push("maxGasten");
  if (!isPosInt(raw.intervalReservatie)) issues.push("intervalReservatie");
  if (!isPosInt(raw.duurReservatie)) issues.push("duurReservatie");

  if (!isYesNo(raw.showNoticeForMaxGuests))
    issues.push("showNoticeForMaxGuests");
  if (!isPhoneish(raw.noticePhoneNumber)) issues.push("noticePhoneNumber");

  if (!isHHmmOrEmpty(raw.ontbijtStop)) issues.push("ontbijtStop");
  if (!isHHmmOrEmpty(raw.lunchStop)) issues.push("lunchStop");
  if (!isHHmmOrEmpty(raw.dinerStop)) issues.push("dinerStop");

  if (!GUEST_MOD_ALLOWED.has(raw.allowGuestCountModification))
    issues.push("allowGuestCountModification");

  if (typeof raw.firewallEnabled !== "boolean") issues.push("firewallEnabled");
  if (!isNonNegInt(raw.storedNumber)) issues.push("storedNumber");

  const done = issues.length === 0;
  const hint = done
    ? "done"
    : `missing/invalid: ${issues.slice(0, 3).join(", ")}${
        issues.length > 3 ? "…" : ""
      }`;

  return { done, hint };
}

/* ---------------- Exceptions validator ---------------- */
const TYPES_ALLOWED = new Set(["Sluiting", "Uitzondering"]);
const TIMEFRAME_ALLOWED = new Set([
  "Volledige Dag",
  "breakfast",
  "lunch",
  "dinner",
]);
const isISODate = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
const cmpDate = (a, b) => new Date(a).getTime() - new Date(b).getTime();
const MIN_EXCEPTIONS = 1;

export function validateExceptions(list) {
  if (!Array.isArray(list)) return { done: false, hint: "not set" };
  if (list.length === 0) return { done: false, hint: "no exceptions" };

  let validCount = 0;
  const issues = [];

  const isHH = (t) => typeof t === "string" && /^\d{2}:\d{2}$/.test(t);
  const toM = (t) => {
    const [h, m] = (t || "0:0").split(":").map(Number);
    return h * 60 + m;
  };

  list.forEach((item, idx) => {
    const i = idx + 1;
    if (!item || typeof item !== "object") {
      issues.push(`#${i}: invalid item`);
      return;
    }

    const titleOk =
      typeof item.title === "string" && item.title.trim().length > 0;
    const typeOk = TYPES_ALLOWED.has(item.type);
    const tfOk = TIMEFRAME_ALLOWED.has(item.timeframe);
    const sdOk = isISODate(item.startDate);
    const edOk = isISODate(item.endDate);
    const rangeOk = sdOk && edOk && cmpDate(item.startDate, item.endDate) <= 0;

    const daysOk = !("daysOfWeek" in item) || Array.isArray(item.daysOfWeek);

    let specificOk = true;

    if (typeOk && item.type === "Uitzondering") {
      const shOk = isHH(item.startHour);
      const ehOk = isHH(item.endHour);
      const hoursOk = shOk && ehOk && toM(item.startHour) < toM(item.endHour);

      const seats = Number(item.maxSeats);
      const seatsOk = Number.isFinite(seats) && seats > 0;

      if (!hoursOk) {
        specificOk = false;
        issues.push(`#${i}: startHour/endHour`);
      }
      if (!seatsOk) {
        specificOk = false;
        issues.push(`#${i}: maxSeats`);
      }
    }

    const bad = [];
    if (!titleOk) bad.push("title");
    if (!typeOk) bad.push("type");
    if (!tfOk) bad.push("timeframe");
    if (!sdOk) bad.push("startDate");
    if (!edOk) bad.push("endDate");
    if (sdOk && edOk && !rangeOk) bad.push("range");
    if (!daysOk) bad.push("daysOfWeek");

    if (bad.length) {
      issues.push(`#${i}: ${bad.join(", ")}`);
    } else if (specificOk) {
      validCount++;
    }
  });

  if (validCount < MIN_EXCEPTIONS) {
    issues.unshift(`add at least ${MIN_EXCEPTIONS} valid exception`);
  }

  const done = issues.length === 0;
  const hint = done
    ? "done"
    : `issues: ${issues.slice(0, 3).join(", ")}${issues.length > 3 ? "…" : ""}`;
  return { done, hint };
}
